// css
import '@/main_ui-styles.js';
// JS
import * as StackTrace from 'stacktrace-js'
import html2canvas from 'html2canvas';

// Vue imports
import { createApp, reactive } from 'vue';
import TTRouter, { VueRouter } from '@/components/TTRouter';
import { createPinia } from 'pinia';
// import Colada, { PiniaColadaPlugin } from 'colada-plugin';

import TTUIWrapper from '@/components/TTUIWrapper.vue';
import TTWindowCaptionButton from '@/components/TTWindowCaptionButton.vue';
import TTEventBus from '@/services/TTEventBus';
import { TTInteractionManagerObj } from '@/services/TTInteractionManager';
import { TTShortcutRegistryObj } from '@/services/TTShortcutRegistry';
import { installTimeTrexVue } from '@/services/TTVueSetup';
import { TTBadgeDirective, TTTooltipDirective } from '@/services/TTVueDirectives';

// Global Vue.js Components
import MessageBox from '@/components/common/MessageBox';

import { Global } from '@/global/Global';
import { TTDate } from '@/global/TTDate';
import { LocalCacheData } from '@/global/LocalCacheData';
import { TTCache, TTMemoryCacheAdapter, TTSessionStorageCacheAdapter } from '@/global/TTCache';
import { TTCurrentSession } from '@/global/TTCurrentSession';
import { TTPersistentSession } from '@/global/TTPersistentSession';
import { Base } from '@/model/Base';
import { ServiceCaller } from '@/services/ServiceCaller';
import { TTAPI } from '@/services/TimeTrexClientAPI';
import { TTWebauthnObj } from '@/services/TTWebauthn';
import { TTSAMLObj } from '@/services/TTSAML';
import { TTUUID } from '@/global/TTUUID';
import { TTPromise } from '@/global/TTPromise';// Potentially the same issue as ProgressBar (multiple instances, no single state), so lets just make it global to be safe.
import { TTAutomation } from '@/global/TTAutomation';
import { TTPresentation } from '@/global/TTPresentation';
import { ProgressBar } from '@/global/ProgressBarManager'; // Must only be imported in one place, otherwise multiple instances could be loaded and clash by leaving open Progressbars. e.g. Seen in TimeSheet
import { PermissionManager } from '@/global/PermissionManager';
import { TAlertManager } from '@/global/TAlertManager';
import { TTi18n } from '@/global/TTi18n.class';
import { useTTViewStackStore } from '@/stores/TTViewStack';

import '@/global/widgets/text_input/TTextInput.js';
import '@/global/widgets/text_input/TPasswordInput.js';
import '@/global/widgets/combobox/TComboBox.js';

// Main execution for imported code
window.html2canvas = html2canvas;
window.StackTrace = StackTrace;
// Function(jQueryBaResize)(); // Need this to execute in correct scope where this=window, otherwise the 'this' value in the jquery plugin results to {} instead of real window, as the 'this' param passed into the plugins IIFE is not window, but {}, which means `window` in plugin = {}. Results in error: "TypeError: window[str_setTimeout] is not a function"

// not needed if cookie script included in html <script>
// window.getCookie = getCookie;
// window.setCookie = setCookie;
// window.deleteCookie = deleteCookie;

// Main execution for imported code
// Define the globals for various plugins and views.
// window.TTBackboneView = TTBackboneView;
// ES module imports do not publish jQuery globally. Legacy third-party plugins still discover
// it through window, so every application entrypoint establishes the same compatibility edge.
window.$ = window.jQuery = $;
window.Global = Global;
// Issue #3812: Qualified TTS calls use the canonical date owner like other named
// browser helpers, without adding individual date methods to every edit's function map.
// Profile Auto-Detect calls it only on click with an empty unavailable-zone fallback;
// the schema changes the unsaved field through the ordinary setter and still requires Save.
window.TTDate = TTDate;
window.TTCache = new TTCache( {
	storage_adapter: new TTMemoryCacheAdapter(),
	namespace: 'TTCurrentSession',
} );
window.TTCurrentSession = new TTCurrentSession(window.TTCache);
window.TTPersistentSession = new TTPersistentSession( new TTCache( {
	storage_adapter: new TTSessionStorageCacheAdapter(),
	namespace: 'TTPersistentSession',
	scope_resolver: () => {
		const session_id = window.TTCurrentSession?.getSessionId?.() ?? null;
		const user_id = window.TTCurrentSession?.getLoginUser?.()?.id ?? null;
		const company_id = window.TTCurrentSession?.getCurrentCompany?.()?.id ?? null;

		if ( !session_id || !user_id || !company_id ) {
			return null;
		}

		return {
			app: 'main_ui',
			session_id: session_id,
			user_id: user_id,
			company_id: company_id,
		};
	},
} ) );
window.LocalCacheData = LocalCacheData;
window.LocalCacheData.isStorageAvailable(); //Check to see if local storage is actually available.
window.Base = Base;
window.ServiceCaller = ServiceCaller;
window.TTAPI = TTAPI;
window.TTUUID = TTUUID;
window.TTPromise = TTPromise;
window.TTAutomation = TTAutomation;
// Issue #3854: Keep presentation capture opt-in and globally scriptable for approved
// demos. Installing before Vue mounts covers both Vue and legacy UI without changing
// either interaction owner; install() restores only an earlier explicit tab enable.
window.TTPresentation = TTPresentation;
window.TTPresentation.install();
window.ProgressBar = ProgressBar;
window.PermissionManager = PermissionManager;
window.TAlertManager = TAlertManager;
window.TTi18n = TTi18n;
//window.TopMenuManager = TopMenuManager;
window.TTWebauthn = TTWebauthnObj;
window.TTSAML = TTSAMLObj;
window.TTEventBus = TTEventBus; // Only used for globals. Make sure to instantiate in every view you want to use.
window.TTRouter = TTRouter;
window.VueRouter = VueRouter; // #VueContextMenu# Allows the TT UI to trigger route changes from within BaseViewController.loadView or elsewhere.

// Covers every current and future Vite dynamic import, including async routes that
// are not routed through Global.importErrorHandler().
window.addEventListener( 'vite:preloadError', function( event ) {
	Global.vitePreloadErrorHandler( event, hasUnsavedChanges );
} );

// TODO: At some point, the code in this file should be refactored into classes.

function onDocumentReady( callback ) {
	if ( document.readyState === 'loading' ) {
		// This replaces the local jQuery ready wrapper only for the main entrypoint.
		// Legacy modules can still depend on jQuery while startup keeps moving toward
		// browser-native DOM lifecycle hooks.
		document.addEventListener( 'DOMContentLoaded', callback, { once: true } );
	} else {
		callback();
	}
}

function hasUnsavedChanges() {
	// Vue and legacy editors coexist during the UI migration. Keep the deployment
	// reload path aligned with beforeunload so a stale chunk never silently loses edits.
	return useTTViewStackStore().hasUnsavedChanges()
		|| ( LocalCacheData.current_open_primary_controller && LocalCacheData.current_open_primary_controller.edit_view && LocalCacheData.current_open_primary_controller.is_changed == true )
		|| ( LocalCacheData.current_open_report_controller && LocalCacheData.current_open_report_controller.is_changed == true )
		|| ( LocalCacheData.current_open_edit_only_controller && LocalCacheData.current_open_edit_only_controller.is_changed == true )
		|| ( LocalCacheData.current_open_sub_controller && LocalCacheData.current_open_sub_controller.edit_view && LocalCacheData.current_open_sub_controller.is_changed == true );
}

window.addEventListener('load', loadViewRequiredJS);

// Moved base_url code from later in the file. Moved earlier in code to before Vue is initialized, otherwise templates that depend on base_url (such as image src's) will get 'null' instead of the correct path.
ServiceCaller.base_url = Global.getBaseURL( '../../', false );
ServiceCaller.base_api_url = 'api/json/api.php';
ServiceCaller.root_url = Global.getRootURL();

// Start of moved loginDate section.
var loginData = {};
//Set in APIGlobal.php
if ( !need_load_pre_login_data ) {
	loginData = APIGlobal.pre_login_data;
} else {
	need_load_pre_login_data = false;
}
if ( !loginData.hasOwnProperty( 'api_base_url' ) ) {
	// Alternate-session bootstrap reaches this branch before onDocumentReady(), so
	// use the imported API owner instead of the callback-local alias declared later.
	TTAPI.APIAuthentication.getPreLoginData( null, {
		showProgressBar: false,
		onResult: function( e ) {

			var result = e.getResult();

			window.TTCurrentSession.setLoginData( result );
			APIGlobal.pre_login_data = result;

			loginData = window.TTCurrentSession.getLoginData();
			// initApps(); // commented out as this is still trigged later on in the code in original place.

		}
	} );
} else {
	window.TTCurrentSession.setLoginData( loginData );
	// initApps(); // commented out as this is still trigged later on in the code in original place.
}
// End of moved loginDate section.


// Vue initialization
const tt_main_ui = createApp( TTUIWrapper );
const pinia = createPinia();

// Install interaction capture before mounting Vue so startup clicks/keys and any
// mount-time failures share the same bounded diagnostic history as the running UI.
// The manager is provided through Vue as well as imported by infrastructure owners;
// this avoids introducing another mutable window global during the legacy transition.
// Install is idempotent against this Document, which matters when Vite re-evaluates the
// entry module: one physical input must always produce one diagnostic activation.
TTInteractionManagerObj.install( {
	app_id: 'main_ui',
	vue_app: tt_main_ui,
} );
// Shortcut dispatch is intentionally a sibling service. The interaction manager may
// observe an executed shortcut but has no authority to choose or run commands.
TTShortcutRegistryObj.install();

// Vue catches component errors before they reach window.onerror. Preserve the
// console diagnostics for developers and route the same failure through the common
// report path, which now obtains sanitized interaction history directly. Passing the
// original Error as argument five preserves the existing source-map/raw-stack pipeline;
// no Vue component instance or reactive state is appended to the report.
tt_main_ui.config.errorHandler = ( err, vm, info ) => {
	console.error( "Error:", err );
	console.error( "Vue component:", vm );
	console.error( "Additional info:", info );
	Global.sendErrorReport(
		'Vue component error: ' + ( err?.message || String( err ) ) + ' (' + info + ')',
		ServiceCaller.root_url,
		'',
		'',
		err
	);
};

tt_main_ui.directive( 'tooltip', TTTooltipDirective );

tt_main_ui.config.globalProperties.Global = Global;
tt_main_ui.config.globalProperties.$appState = reactive({ colorScheme: 'light' });

// Global Vue Components Registration
tt_main_ui.component( 'MessageBox', MessageBox );
// Window caption controls are shared by the top-level manager and workflow-owned shells such as TTWizard, which cannot inherit the manager's local component registry.
tt_main_ui.component( 'TTWindowCaptionButton', TTWindowCaptionButton );

tt_main_ui.use( pinia );
// pinia.use( PiniaColadaPlugin ); // Currently not using Colada, as its causing some JSON errors in the console. Bug reported at https://github.com/oslabs-beta/colada/issues/59
// tt_main_ui.use( Colada );

installTimeTrexVue( tt_main_ui, {
	inputStyle: 'outlined'
} );
tt_main_ui.directive( 'badge', TTBadgeDirective );

//VueRouter.isReady().then(() => app.mount('#app'))
tt_main_ui.use( VueRouter )
	.mount( '#tt_main_ui' );
// End of Vue initialization

//Don't not show loading bar if refresh
if ( Global.isSet( window.TTCurrentSession.getLoginUser() ) ) {
	var loading_view = document.querySelector( '.loading-view' );
	if ( loading_view ) {
		loading_view.style.display = 'none';
	}
} else {
	setProgress();
}

function setProgress() {
	window.loading_bar_time = setInterval( function() {
		var progress_bar = document.querySelector( '.progress-bar' );
		if ( !progress_bar ) {
			return;
		}

		var c_value = Number( progress_bar.value );

		if ( c_value < 90 ) {
			progress_bar.value = c_value + 10;
		}
	}, 1000 );
}

function cleanProgress() {
	var progress_bar = document.querySelector( '.progress-bar' );
	if ( progress_bar ) {
		progress_bar.value = 100;
	}
	// cleanProgress() runs on both initial startup and refresh, where the timer may
	// never have been started. Keep the path idempotent instead of recreating
	// jQuery's :visible behavior for this one startup overlay.
	window.clearInterval( window.loading_bar_time );

	window.loading_bar_time = window.setTimeout( function() {
		var progress_bar_div = document.querySelector( '.progress-bar-div' );
		if ( progress_bar_div ) {
			progress_bar_div.style.display = 'none';
		}
		window.loading_bar_time = null;
	}, 50 );
}

window.is_browser_iOS = ( navigator.userAgent.match( /(iPad|iPhone|iPod)/g ) ? true : false );

onDocumentReady( function() {
	Global.styleSandbox();

	cleanProgress();

	var api_authentication = TTAPI.APIAuthentication;

	if ( Error ) {
		Error.stackTraceLimit = 50; //Increase JS exception stack trace limit.
	}

	//BUG-2065 see also: require.onError()
	window.onerror = function( error_msg, file, line, col, error_obj ) {
		if ( !arguments || arguments.length < 1 ) {
			Global.sendErrorReport( 'No error parameters when window.onerror', ServiceCaller.root_url, '', '', '' );
		} else {
			Global.sendErrorReport( error_msg, file, line, col, error_obj );
		}
	};

	window.addEventListener( 'beforeunload', function( e ) {
		// Note that Google recommends against the following, as it affects page caching, but we dont want caching anyway: https://developers.google.com/web/updates/2018/07/page-lifecycle-api#the-beforeunload-event
		Global.sendAnalyticsEvent( 'browser', 'browser:beforeunload', 'browser:beforeunload' );
		// Vue edit state is derived from live factories and inside editors rather
		// than the removed legacy controller flags. Check both during the hybrid
		// transition so refresh cannot silently discard a mounted Vue editor.
		if ( hasUnsavedChanges() ) {
			e.preventDefault(); // Cancel the unload event
			e.returnValue = ''; // Chrome requires returnValue to be set
		}
	} );

	//-vue $( 'body' ).addClass( 'login-bg' );

	//Load need API class

	// Global application shortcuts are registered declaratively in one owner. The
	// bubbling registry deliberately runs after focused widgets/components so their
	// local key handling can prevent an application command when appropriate.
	TTShortcutRegistryObj.register( {
		id: 'prevent-browser-backspace-navigation',
		key: 'Backspace',
		// Browser history bindings vary by platform. Preserve the legacy guard for
		// every Backspace modifier combination outside an editable control.
		allow_extra_modifiers: true,
		allow_in_editable: false,
		handler: function() {
			// prevent_default is the command: Backspace outside an editor must never
			// navigate away from unsaved application state.
		},
	} );
	// Keep selection behavior in its existing `selectAll()` command owner. The registry
	// arbitrates only platform modifiers/focus and contributes an executed-command
	// breadcrumb; it does not inspect rows, permissions, alerts, or active view state.
	TTShortcutRegistryObj.register( {
		id: 'select-all-visible-records',
		key: 'a',
		mod: true,
		// Alt/Shift variants remain available to browser/accessibility conventions and
		// cannot accidentally invoke the application-wide record selection command.
		alt_key: false,
		shift_key: false,
		allow_in_editable: false,
		handler: function() {
			selectAll();
		},
	} );

	// Moved earlier in code to before Vue is initialized, otherwise templates that depend on base_url (such as image src's) will get 'null' instead of the correct path.
	// ServiceCaller.base_url = Global.getBaseURL( '../../', false );
	// ServiceCaller.base_api_url = 'api/json/api.php';
	// ServiceCaller.root_url = Global.getRootURL();

	// var loginData = {};
	// //Set in APIGlobal.php
	// if ( !need_load_pre_login_data ) {
	// 	loginData = APIGlobal.pre_login_data;
	// } else {
	// 	need_load_pre_login_data = false;
	// }
	// if ( !loginData.hasOwnProperty( 'api_base_url' ) ) {
	// 	api_authentication.getPreLoginData( null, {
	// 		onResult: function( e ) {
	//
	// 			var result = e.getResult();
	//
	//			window.
	//			window.TTCurrentSession.setLoginData( result );
	// 			APIGlobal.pre_login_data = result;
	//
	// 			loginData = window.TTCurrentSession.getLoginData();
	// 			initApps();
	//
	// 		}
	// 	} );
	// } else {
	// 	window.TTCurrentSession.setLoginData( loginData ); //set here because the loginData is set from php
	// 	initApps();
	// }
	// initApps() now waits for async language loading, so defer analytics until startup has finished and route/static strings are stable.
	initApps().then( initAnalytics ).catch( handleInitAppsError );

	function isAutomatedBrowserSession() {
		// Selenium exposes navigator.webdriver before Global.setUnitTestMode() can run; skip external analytics early so browser-log checks stay deterministic.
		return ( typeof Global != 'undefined' && Global.UNIT_TEST_MODE === true ) || ( typeof navigator != 'undefined' && navigator.webdriver === true );
	}

	function initAnalytics() {
		/* jshint ignore:start */
		if ( APIGlobal.pre_login_data.analytics_enabled === true && isAutomatedBrowserSession() === false && ServiceCaller && ServiceCaller.root_url && loginData && loginData.base_url ) {
			if ( APIGlobal.pre_login_data.analytics_tracking_code != '' ) {
				try {
					var gtag_script = document.createElement( 'script' );
					gtag_script.setAttribute( 'src', 'https://www.googletagmanager.com/gtag/js?id=' + APIGlobal.pre_login_data.analytics_tracking_code ); //The JS isn't static, so load it remotely instead.
					document.head.appendChild( gtag_script );

					window.dataLayer = window.dataLayer || [];
					window.gtag = function gtag() {
						window.dataLayer.push( arguments );
					}
					gtag( 'js', new Date() );
					gtag( 'config', APIGlobal.pre_login_data.analytics_tracking_code, {
						'debug_mode': !APIGlobal.pre_login_data.production,
						'send_page_view': false
					} );

					//Do not check exitstance of LocalCacheData with if(LocalCacheData) or JS will execute the unnamed function it uses as a constructor
					if ( window.TTCurrentSession.getLoginUser() ) {
						var current_company = window.TTCurrentSession.getCurrentCompany();
						Global.setAnalyticDimensions( window.TTCurrentSession.getLoginUser().first_name + ' (' + window.TTCurrentSession.getLoginUser().id + ')', current_company.name );
					} else {
						Global.setAnalyticDimensions();
					}
				} catch ( e ) {
					throw e; //Attempt to catch any errors thrown by Google Analytics.
				}
			}

			if ( APIGlobal.pre_login_data.ui_tracking_code != '' ) {
				try {
					var script = document.createElement( 'script' );
					script.type = 'text/javascript';
					script.async = true;
					script.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "` + APIGlobal.pre_login_data.ui_tracking_code + `");`;
					document.head.appendChild( script );
				} catch ( e ) {
					throw e; //Attempt to catch any errors thrown.
				}
			}
		}
		/* jshint ignore:end */
	}

	function handleInitAppsError( e ) {
		// Preserve the existing startup error-reporting path now that initApps can fail from a promise continuation.
		// Throwing from a timer re-enters window.onerror instead of leaving a rejected promise that the legacy error reporter would miss.
		window.setTimeout( function() {
			throw e;
		}, 0 );
	}

	async function initApps() {
		TAlertManager.showBrowserTopBanner(); //Checks for supported browser versions and display banner if not.

		// 	loadViewRequiredJS(); // Moved to top of document, waiting on 'load' completion, to load after the 'red line'

		// Load the dictionary before Global.initStaticStrings(); static strings are copied once and would otherwise keep untranslated labels.
		// TTi18n handles null/en_US/no-load cases too, so entrypoints do not need to repeat locale guard logic.
		var language_result = await TTi18n.loadLanguage( loginData.locale, Global.getLanguageLoadOptions( loginData ) );
		if ( language_result.shouldLoad === true ) {
			Debug.Text( 'Using Locale: ' + loginData.locale, 'main.js', '', 'initApps', 1 );
		}
		// This must remain after TTi18n.loadLanguage() so Global.any_item, Global.default_item, alert text, etc. are initialized with the active dictionary.
		Global.initStaticStrings();

		window.TTCurrentSession.setDeploymentOnDemand( loginData.deployment_on_demand );
		window.TTCurrentSession.setProductEditionId( loginData.product_edition );

		var alternate_session_data = getCookie( 'AlternateSessionData' );
		if ( alternate_session_data ) {
			try { //Prevent JS exception if we can't parse alternate_session_data for some reason.
				alternate_session_data = JSON.parse( alternate_session_data );
				if ( alternate_session_data && alternate_session_data.previous_session_id ) {
					TAlertManager.showPreSessionAlert();
				}
			} catch ( e ) {
				Debug.Text( e.message, 'main.js', 'require', 'initApps', 10 );
			}
		}

		//When the user switched away from, then back again to our tab, make sure the session is still the same so they don't get two different login sessions confused.
		function handleVisibilityChange() {
			//No need to handle anything if installer is enabled.
			if ( APIGlobal.pre_login_data && APIGlobal.pre_login_data.installer_enabled && APIGlobal.pre_login_data.installer_enabled == true ) {
				return null;
			}

			var is_hidden = document.hidden;
			var cookie_session_id = getCookie( Global.getSessionIDKey() );

			Debug.Text( 'Tab Visibility Change: ' + is_hidden + ' Session ID: ' + window.TTCurrentSession.getSessionId(), 'main.js', '', 'handleVisibilityChange', 10 );

			if ( is_hidden == false ) {
				//Check to make sure our session_id matches what the server returns.
				//  When duplicating a tab or middle clicking a menu item, then switching to the new tab immediately, TTCurrentSession.getSessionId() could be blank.
				//    So since its doing a browser refresh anyways, prevent that from triggering this message and another reload.
				if ( window.TTCurrentSession.getSessionId() != '' && cookie_session_id != window.TTCurrentSession.getSessionId() ) {
					Debug.Text( 'Session ID has changed out from out underneath us! Session ID: Memory: ' + window.TTCurrentSession.getSessionId() + ' Cookie: ' + cookie_session_id, 'main.js', '', 'handleVisibilityChange', 1 );

					var api = TTAPI.APIAuthentication;
					api.isLoggedIn( false, {
						onResult: function( result ) {
							var result_data = result.getResult();

							if ( result_data === true && cookie_session_id != window.TTCurrentSession.getSessionId() ) { //Recheck the cookie/session ID as they could have changed by this point due to the API call taking some time to finish.
								TAlertManager.showAlert( TTi18n.getText( 'It appears that you have logged in from another web browser window or tab.<br><br>Please be patient while the session is resumed here...' ), 'Session Changed', function() {
									Global.sendAnalyticsEvent( 'session', 'session:changed', 'session:changed' );
									window.location.reload( true );
								} );
							} else {
								//Don't do Logout here, as we need to display a "Session Expired" message to the user, which is triggered from the ServiceCaller.
								//  In order to trigger that though, we need to make an *Authenticated* API call to APIMisc.Ping(), rather than UnAuthenticated call to APIAuthentication.Ping()
								var api = TTAPI.APIMisc;
								api.ping( {
									onResult: function() {
									}
								} );
							}
						}
					} );
				}
			}
		}

		window.addEventListener( 'visibilitychange', handleVisibilityChange );
	}

	function selectAll() {
		//Check for open alert dialog and select all text in it.
		let alert = document.querySelector( '.t-alert' );
		if ( alert ) {
			let content_div = alert.querySelector( '.content-div' );
			if ( content_div ) {
				let range = document.createRange();
				range.selectNodeContents( content_div );
				let selection = window.getSelection();
				selection.removeAllRanges();
				selection.addRange( range );
				return;
			}
		}
	};

} );

function loadViewRequiredJS() {
	window.TTCurrentSession.setLoadViewRequiredJsReady( false ); // #2848 Set to true was moved to post-login-main_ui-dependancies.js to ensure all dependancies fully loaded.

	import(
		'@/post-login-main_ui-vendor-dependancies.js'
	).catch( Global.importErrorHandler );
}
